import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-y-6" }
const _hoisted_2 = { class: "flex flex-col min-h-[300px]" }

import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useFacialBiometryStore } from '@/pinia-store/useFacialBiometryStore'
import type { FacialBiometryStatus } from '@/constants/facial-biometry.constants'
import type {
  FacialBiometryDoctorItem,
  FacialBiometryListParams
} from '@/types/facial-biometry.types'
import FacialBiometryFilterBar from './components/FacialBiometryFilterBar.vue'
import FacialBiometryTable from './components/FacialBiometryTable.vue'
import FacialBiometryTableSkeleton from './components/FacialBiometryTableSkeleton.vue'
import FacialBiometryEmptyState from './components/FacialBiometryEmptyState.vue'
import ValidationDetailModal from './components/ValidationDetailModal.vue'
import ValidationHistoryModal from './components/ValidationHistoryModal.vue'
import ConfirmDeleteAction from '@/components/designSystem/modals/confirm.delete.action.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const store = useFacialBiometryStore()

const { doctors, meta, isLoading } = storeToRefs(store)

const filterBarRef = ref<InstanceType<typeof FacialBiometryFilterBar> | null>(null)
const showValidationModal = ref(false)
const showHistoryModal = ref(false)
const showNotifyConfirmModal = ref(false)
const selectedDoctor = ref<FacialBiometryDoctorItem | null>(null)
const doctorToNotify = ref<FacialBiometryDoctorItem | null>(null)

const notifyConfirmDescription = computed(() => {
  if (!doctorToNotify.value) return ''
  return `Deseja notificar o médico ${doctorToNotify.value.doctor.name} para enviar a biometria facial?`
})

onMounted(async () => {
  await store.fetchDoctors()
})

function applyFilter(filters: Partial<FacialBiometryListParams>) {
  store.setFilters(filters)
  store.fetchDoctors()
}

function handleSearch(query: string) {
  applyFilter({ search: query || undefined })
}

function handleFilterStatus(value: string) {
  applyFilter({ status: value ? (value as FacialBiometryStatus) : null })
}

function handleFilterDate(payload: { start: Date | null; end: Date | null }) {
  applyFilter({
    startDate: payload.start?.toISOString(),
    endDate: payload.end?.toISOString()
  })
}

function handleFilterSchedule(value: string) {
  applyFilter({ scheduleIds: value ? [Number(value)] : undefined })
}

function handleFilterUnit(value: string) {
  applyFilter({ healthUnitIds: value ? [Number(value)] : undefined })
}

function handleSort(order: string) {
  applyFilter({ sortOrder: order as 'ASC' | 'DESC' })
}

function handleChangePage(page: number) {
  store.setPage(page)
  store.fetchDoctors(page)
}

function handleClearFilters() {
  store.resetFilters()
  filterBarRef.value?.reset()
  store.fetchDoctors()
}

function handleEvaluate(item: FacialBiometryDoctorItem) {
  store.initValidationQueue(doctors.value)

  const idx = store.validationQueue.findIndex((d) => d.id === item.id)
  if (idx >= 0) {
    store.setStepIndex(idx)
  }

  showValidationModal.value = true
}

function handleViewHistory(item: FacialBiometryDoctorItem) {
  selectedDoctor.value = item
  showHistoryModal.value = true
}

function handleNotify(item: FacialBiometryDoctorItem) {
  doctorToNotify.value = item
  showNotifyConfirmModal.value = true
}

async function confirmNotify() {
  if (!doctorToNotify.value) return
  await store.notifyDoctor(doctorToNotify.value.doctor.id)
  closeNotifyModal()
}

function closeNotifyModal() {
  showNotifyConfirmModal.value = false
  doctorToNotify.value = null
}

function handleValidated() {
  store.fetchDoctors()
}

return (_ctx: any,_cache: any) => {
  const _component_v_pagination = _resolveComponent("v-pagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(FacialBiometryFilterBar, {
      ref_key: "filterBarRef",
      ref: filterBarRef,
      onSearch: handleSearch,
      onFilterStatus: handleFilterStatus,
      onFilterDate: handleFilterDate,
      onFilterSchedule: handleFilterSchedule,
      onFilterUnit: handleFilterUnit,
      onSort: handleSort
    }, null, 512),
    _createElementVNode("div", _hoisted_2, [
      (_unref(isLoading))
        ? (_openBlock(), _createBlock(FacialBiometryTableSkeleton, { key: 0 }))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (_unref(doctors).length > 0)
              ? (_openBlock(), _createBlock(FacialBiometryTable, {
                  key: 0,
                  doctors: _unref(doctors),
                  onEvaluate: handleEvaluate,
                  onViewHistory: handleViewHistory,
                  onNotify: handleNotify
                }, null, 8, ["doctors"]))
              : (_openBlock(), _createBlock(FacialBiometryEmptyState, {
                  key: 1,
                  onClearFilters: handleClearFilters
                }))
          ], 64))
    ]),
    (!_unref(isLoading) && _unref(doctors).length > 0)
      ? (_openBlock(), _createBlock(_component_v_pagination, {
          key: 0,
          "model-value": _unref(meta).page,
          "total-visible": 5,
          length: _unref(meta).totalPages,
          density: "comfortable",
          color: "primary",
          "data-testid": "biometry-pagination",
          "onUpdate:modelValue": handleChangePage
        }, null, 8, ["model-value", "length"]))
      : _createCommentVNode("", true),
    _createVNode(ValidationDetailModal, {
      modelValue: showValidationModal.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showValidationModal).value = $event)),
      onValidated: handleValidated
    }, null, 8, ["modelValue"]),
    _createVNode(ValidationHistoryModal, {
      modelValue: showHistoryModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((showHistoryModal).value = $event)),
      doctor: selectedDoctor.value
    }, null, 8, ["modelValue", "doctor"]),
    _createVNode(ConfirmDeleteAction, {
      show: showNotifyConfirmModal.value,
      title: "Confirmar notificação",
      description: notifyConfirmDescription.value,
      "text-button": "Notificar",
      onCloseModal: closeNotifyModal,
      onHandleConfirm: confirmNotify
    }, null, 8, ["show", "description"])
  ]))
}
}

})